import type { Asset } from '@odo/shared-ui';
import { CREATION_PHOTO_SLOT_IDS, type CreationPhotoSlotId } from './photoSlots';

export interface SubmissionFiles {
    cbct: readonly Asset[];
    radiographs: readonly Asset[];
    stls: readonly Asset[];
    photoSlots: Readonly<Record<CreationPhotoSlotId, Asset | null>>;
    /** Photos with no creation-flow slot, plus older duplicates of a filled slot. */
    otherPhotos: readonly Asset[];
}

function createdAtMs(asset: Asset): number {
    const ms = asset.createdAt ? Date.parse(asset.createdAt) : Number.NaN;
    return Number.isNaN(ms) ? 0 : ms;
}

function newestFirst(assets: readonly Asset[]): Asset[] {
    return [...assets].sort((a, b) => createdAtMs(b) - createdAtMs(a));
}

function ofCategory(assets: readonly Asset[], category: string): Asset[] {
    return newestFirst(assets.filter((a) => a.storageCategory === category));
}

function photoTypeOf(asset: Asset): unknown {
    return asset.metadata?.photoType;
}

/**
 * Buckets the patient's GCS files the way the dashboard creation flow reads
 * them: radiographs/CBCT/STL by storage category, and PHOTO files into the
 * creation slots by their exact `metadata.photoType` (the ids the dashboard
 * writes). The newest photo wins a slot.
 */
export function groupSubmissionFiles(assets: readonly Asset[]): SubmissionFiles {
    const photos = ofCategory(assets, 'PHOTO');
    const photoSlots = Object.fromEntries(
        CREATION_PHOTO_SLOT_IDS.map((slotId) => [
            slotId,
            photos.find((p) => photoTypeOf(p) === slotId) ?? null
        ])
    ) as Record<CreationPhotoSlotId, Asset | null>;
    const slotted = new Set(Object.values(photoSlots).filter((a): a is Asset => a !== null));

    return {
        cbct: ofCategory(assets, 'CBCT'),
        radiographs: ofCategory(assets, 'RADIOGRAPH'),
        stls: ofCategory(assets, 'STL'),
        photoSlots,
        otherPhotos: photos.filter((p) => !slotted.has(p))
    };
}

/** Every file the viewer renders, so thumbnails are resolved for exactly these. */
export function submissionAssets(files: SubmissionFiles): Asset[] {
    const slottedPhotos = Object.values(files.photoSlots).filter((a): a is Asset => a !== null);
    return [
        ...files.radiographs,
        ...files.cbct,
        ...files.stls,
        ...slottedPhotos,
        ...files.otherPhotos
    ];
}
